import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { X, Heart, Sparkles, Star, ShoppingCart, DollarSign, Crown, ChevronLeft, ChevronRight, MapPin, User, RotateCcw, Info } from 'lucide-react';
import { useAuth, type ShippingAddress } from '../contexts/AuthContext';
import { getAuthApiUrl } from '../lib/apiConfig';
import Header from '../components/Header';
import CartOverlay from '../components/CartOverlay';
import VIPUpgradeModal from '../components/VIPUpgradeModal';
import QuantityPopup from '../components/QuantityPopup';
import CartValidationModal from '../components/CartValidationModal';
import NeedAddressModal from '../components/NeedAddressModal';
import CheckoutAddressModal from '../components/CheckoutAddressModal';
import {
  addVipPlanToCart,
  fetchVipPlans,
  removeCartProduct,
} from '../lib/vipApi';

interface Product {
  id: number;
  name: string;
  description: string;
  category: {
    name: string;
    slug: string;
  };
  vendor: {
    name: string;
    slug: string;
  };
  original_price: number;
  mystery_price: number;
  condition: string;
  mystery_type: string;
  main_image: string;
  mystery_image: string;
  mystery_hint?: string;
  quantity_available: number;
  discount_percentage: number;
  is_mystery_card: boolean;
  display_image: string;
  display_name: string;
  display_price: number;
  quantity?: number;
}

interface UserStatus {
  daily_swipes_used: number;
  daily_swipes_remaining: number;
  is_vip: boolean;
  has_seen_all_products: boolean;
  cart_product_ids: number[];
  is_new_day: boolean;
}

interface FeedStatus {
  has_more_products: boolean;
  message: string;
  show_load_more: boolean;
  show_vip_upgrade: boolean;
  total_available_products: number;
  products_seen: number;
}

interface ProductDetails {
  id: number;
  name: string;
  description: string;
  mystery_price: number;
  original_price: number;
  savings: number;
  savings_percentage: number;
  condition: string;
  packaging_condition?: string;
  condition_disclosure?: string;
  rating: number;
  category: {
    name: string;
    slug: string;
  };
  vendor: {
    name: string;
    rating: number;
    location: string;
  };
  images: string[];
  is_mystery: boolean;
  mystery_type: string;
  quantity_available: number;
  is_in_cart: boolean;
  cart_quantity: number;
  created_at: string;
  updated_at: string;
}


interface SmartFeedResponse {
  success: boolean;
  products: Product[];
  user_status: UserStatus;
  feed_status: FeedStatus;
}

/** Normalize API prices that may be numbers or strings like "₹500.00" (avoids "₹₹" in UI). */
function rupeeAmount(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = parseFloat(value.replace(/[₹,\s]/g, ''));
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function formatRupee(value: unknown): string {
  return rupeeAmount(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

// ProductDetailsModal Component
const ProductDetailsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  productId: number | null;
  onAddToCart: (productId: number) => void;
  onContinueSwiping: () => void;
}> = ({ isOpen, onClose, productId, onAddToCart, onContinueSwiping }) => {
  const [product, setProduct] = useState<ProductDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Fetch product details when modal opens
  useEffect(() => {
    if (isOpen && productId) {
      fetchProductDetails();
    }
  }, [isOpen, productId]);

  const fetchProductDetails = async () => {
    if (!productId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/products/${productId}/details/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch product details');
      }

      const data = await response.json();
      if (data.success) {
        setProduct(data.product);
        setCurrentImageIndex(0);
      } else {
        throw new Error(data.message || 'Failed to load product details');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      onAddToCart(product.id);
    }
  };

  const nextImage = () => {
    if (product && product.images.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === product.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (product && product.images.length > 1) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? product.images.length - 1 : prev - 1
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-cosmic-card rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-cosmic-border">
          <h2 className="text-xl font-bold text-white">Product Details</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cosmic-primary"></div>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={fetchProductDetails}
                className="btn-cosmic"
              >
                Try Again
              </button>
            </div>
          )}

          {product && !loading && (
            <>
              {/* Image Carousel */}
              <div className="relative mb-6">
                <div className="aspect-square bg-gray-800 rounded-xl overflow-hidden">
                  {product.images.length > 0 ? (
                    <img
                      src={product.images[currentImageIndex]}
                      alt={product.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      No Image Available
                    </div>
                  )}
                </div>

                {/* Image Navigation */}
                {product.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}

                {/* Image Dots */}
                {product.images.length > 1 && (
                  <div className="flex justify-center mt-2 space-x-2">
                    {product.images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentImageIndex ? 'bg-cosmic-primary' : 'bg-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="space-y-4">
                {/* Title and Condition */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-xl font-bold text-white">{product.name}</h3>
                    <span className="px-2 py-1 bg-green-500 text-black text-xs font-semibold rounded-full">
                      {product.condition}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <div className="flex items-center gap-1">
                      <Star size={16} className="text-yellow-400 fill-current" />
                      <span>{product.rating}</span>
                    </div>
                    <span>•</span>
                    <span>{product.category.name}</span>
                  </div>
                </div>

                {/* Pricing */}
                <div className="bg-cosmic-background/50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-bold text-cosmic-primary">
                        ₹{product.mystery_price.toLocaleString()}
                      </span>
                      {product.original_price > product.mystery_price && (
                        <span className="text-lg text-gray-400 line-through">
                          ₹{product.original_price.toLocaleString()}
                        </span>
                      )}
                    </div>
                    {product.savings > 0 && (
                      <span className="px-2 py-1 bg-red-500 text-white text-xs font-semibold rounded-full">
                        {product.savings_percentage}% OFF
                      </span>
                    )}
                  </div>
                  {product.savings > 0 && (
                    <p className="text-green-400 text-sm font-semibold">
                      You save ₹{product.savings.toLocaleString()}!
                    </p>
                  )}
                </div>

                {/* Packaging-condition disclosure (only for damaged packaging) */}
                {product.packaging_condition === 'packaging_damaged' && product.condition_disclosure && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-start gap-2">
                    <Info size={16} className="text-amber-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-amber-200/90">{product.condition_disclosure}</p>
                  </div>
                )}

                {/* Vendor Information */}
                <div className="bg-cosmic-background/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <User size={16} className="text-cosmic-primary" />
                    <span className="font-semibold text-white">Seller Information</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-white font-medium">{product.vendor.name}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <div className="flex items-center gap-1">
                        <Star size={14} className="text-yellow-400 fill-current" />
                        <span>{product.vendor.rating}</span>
                      </div>
                      <span>•</span>
                      <div className="flex items-center gap-1">
                        <MapPin size={14} />
                        <span>{product.vendor.location}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h4 className="font-semibold text-white mb-2">Description</h4>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {product.description}
                  </p>
                </div>

                {/* Mystery Card Info */}
                {product.is_mystery && (
                  <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-500/30">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">🎁</span>
                      <span className="font-semibold text-white">Mystery Card</span>
                    </div>
                    <p className="text-sm text-gray-300">
                      This is a mystery card! The exact product will be revealed after purchase.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer Actions */}
        {product && !loading && (
          <div className="p-4 border-t border-cosmic-border bg-cosmic-background/30">
            <div className="space-y-3">
              {product.is_in_cart ? (
                <div className="text-center py-2">
                  <p className="text-green-400 font-semibold flex items-center justify-center gap-2">
                    <ShoppingCart size={16} />
                    Already in Cart ({product.cart_quantity} items)
                  </p>
                </div>
              ) : (
                <button
                  onClick={handleAddToCart}
                  className="w-full btn-cosmic flex items-center justify-center gap-2"
                >
                  <ShoppingCart size={16} />
                  Add to Cart - ₹{product.mystery_price.toLocaleString()}
                </button>
              )}
              
              <button
                onClick={onContinueSwiping}
                className="w-full btn-mystery flex items-center justify-center gap-2"
              >
                <RotateCcw size={16} />
                Continue Swiping
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CHECKOUT_SHIPPING_FEE = 50;

export default function SwipeInterface() {
  // console.log('SwipeInterface component loaded!'); // Debug: Component loading
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, token, user, updateDailySwipes, refreshUserProfile } = useAuth();
  
  // Backend cart sync functions
  const addToBackendCart = async (productId: number, quantity: number = 1, replaceQuantity: boolean = false) => {
    if (!token) return;
    
    try {
      const response = await fetch('/api/cart/add_item/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          product_id: productId,
          quantity: quantity,
          replace_quantity: replaceQuantity
        })
      });
      
      if (response.ok) {
        console.log('Item added to backend cart');
        // Reload cart from backend to get updated state
        await loadCartFromBackend();
      } else {
        console.error('Failed to add item to backend cart');
      }
    } catch (error) {
      console.error('Error adding to backend cart:', error);
    }
  };

  const removeFromBackendCart = async (productId: number) => {
    if (!token) return;
    
    try {
      const response = await fetch('/api/cart/remove_item/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          product_id: productId
        })
      });
      
      if (response.ok) {
        console.log('Item removed from backend cart');
        // Reload cart from backend to get updated state
        await loadCartFromBackend();
      } else {
        console.error('Failed to remove item from backend cart');
      }
    } catch (error) {
      console.error('Error removing from backend cart:', error);
    }
  };

  const updateBackendCartQuantity = async (productId: number, quantity: number) => {
    if (!token) return;
    
    // Find the cart item to check available quantity
    const cartItem = cartItems.find(item => item.id === productId);
    if (cartItem && cartItem.quantity_available && quantity > cartItem.quantity_available) {
      console.warn(`Cannot update quantity to ${quantity}, maximum available is ${cartItem.quantity_available}`);
      return;
    }
    
    try {
      const response = await fetch('/api/cart/update_quantity/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          product_id: productId,
          quantity: quantity
        })
      });
      
      if (response.ok) {
        console.log('Cart quantity updated successfully');
        // Reload cart from backend to get updated state
        await loadCartFromBackend();
    } else {
        console.error('Failed to update cart quantity');
      }
    } catch (error) {
      console.error('Error updating cart quantity:', error);
    }
  };

  const validateCartAvailability = async () => {
    if (!token) return { hasIssues: false, itemsToRemove: [], itemsToAdjust: [] };
    
    try {
      const response = await fetch('/api/cart/validate_availability/', {
        method: 'GET',
            headers: {
              'Authorization': `Token ${token}`,
            },
          });
          
          if (response.ok) {
            const data = await response.json();
            return {
          hasIssues: data.has_issues,
          itemsToRemove: data.items_to_remove || [],
          itemsToAdjust: data.items_to_adjust || []
        };
      } else {
        console.error('Failed to validate cart availability');
        return { hasIssues: false, itemsToRemove: [], itemsToAdjust: [] };
      }
        } catch (error) {
      console.error('Error validating cart availability:', error);
      return { hasIssues: false, itemsToRemove: [], itemsToAdjust: [] };
    }
  };

  const removeUnavailableItems = async (productIds: number[]) => {
    if (!token) return;
    
    try {
      const response = await fetch('/api/cart/remove_unavailable_items/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          product_ids: productIds
        })
      });
      
      if (response.ok) {
        console.log('Unavailable items removed successfully');
        await loadCartFromBackend();
      } else {
        console.error('Failed to remove unavailable items');
      }
    } catch (error) {
      console.error('Error removing unavailable items:', error);
    }
  };

  const adjustCartQuantities = async (adjustments: Array<{product_id: number, new_quantity: number}>) => {
    if (!token) return;
    
    try {
      const response = await fetch('/api/cart/adjust_quantities/', {
        method: 'POST',
        headers: {
        'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({
          adjustments: adjustments
        })
      });
      
      if (response.ok) {
        console.log('Cart quantities adjusted successfully');
        await loadCartFromBackend();
      } else {
        console.error('Failed to adjust cart quantities');
      }
    } catch (error) {
      console.error('Error adjusting cart quantities:', error);
    }
  };

  const loadCartFromBackend = async () => {
    if (!token) return;
    
    try {
      const response = await fetch('/api/cart/get_cart/', {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Cart loaded from backend:', data);
        
        // Extract cart items from the response
        // The cart API returns: {success: true, cart: {id: 14, items: [...], ...}}
        if (data && data.success && data.cart && data.cart.items) {
          const cart = data.cart;
          if (cart.items && cart.items.length > 0) {
            const cartProducts = cart.items.map((item: any) => ({
              id: item.product.id,
              display_name: item.product.name,
              description: item.product.description,
              original_price: typeof item.product.original_price === 'string' ? parseFloat(item.product.original_price) : item.product.original_price,
              mystery_price: typeof item.product.mystery_price === 'string' ? parseFloat(item.product.mystery_price) : item.product.mystery_price,
              display_price: typeof item.product.mystery_price === 'string' ? parseFloat(item.product.mystery_price) : item.product.mystery_price, // Use mystery_price as display_price
              display_image: item.product.main_image || '',
              category: item.product.category,
              is_mystery_card: item.product.is_mystery_card,
              quantity: item.quantity,
              quantity_available: item.product.quantity_available || 1
            }));
            
            setCartItems(cartProducts);
            console.log('Cart items set from backend:', cartProducts);
            
            // Calculate total savings from backend cart
            const totalSavingsFromCart = cartProducts.reduce((sum: number, item: any) => {
              const originalPrice = item.original_price || item.display_price;
              const currentPrice = item.display_price;
              return sum + ((originalPrice - currentPrice) * (item.quantity || 1));
            }, 0);
            setTotalSavings(totalSavingsFromCart);
          } else {
            console.log('Cart is empty');
            setCartItems([]);
            setTotalSavings(0);
          }
        } else {
          console.log('No cart found');
          setCartItems([]);
          setTotalSavings(0);
        }
      } else {
        console.error('Failed to load cart from backend');
      }
    } catch (error) {
      console.error('Error loading cart from backend:', error);
    }
  };

  const [products, setProducts] = useState<Product[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cartItems, setCartItems] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessingSwipe, setIsProcessingSwipe] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isVIPModalOpen, setIsVIPModalOpen] = useState(false);
  const [totalSavings, setTotalSavings] = useState(0);
  const [swipeLimitReached, setSwipeLimitReached] = useState(false);
  const [noProductsAvailable, setNoProductsAvailable] = useState(false);
  const [isProductDetailsOpen, setIsProductDetailsOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  
  // Bumble-style stack animation state
  const [nextProductIndex, setNextProductIndex] = useState(1);
  
  // Quantity popup state
  const [isQuantityPopupOpen, setIsQuantityPopupOpen] = useState(false);
  const [quantityPopupData, setQuantityPopupData] = useState<{
    productName: string;
    quantityAvailable: number;
    message: string;
    maxQuantity: number;
  } | null>(null);
  const [pendingSwipeDirection, setPendingSwipeDirection] = useState<'left' | 'right' | null>(null);
  
  // Cart validation modal state
  const [isCartValidationModalOpen, setIsCartValidationModalOpen] = useState(false);
  const [cartValidationData, setCartValidationData] = useState<{
    itemsToRemove: Array<{
      product_id: number;
      product_name: string;
      reason: string;
      message: string;
    }>;
    itemsToAdjust: Array<{
      product_id: number;
      product_name: string;
      reason: string;
      message: string;
      requested_quantity: number;
      available_quantity: number;
    }>;
  }>({
    itemsToRemove: [],
    itemsToAdjust: []
  });

  const [needAddressModalOpen, setNeedAddressModalOpen] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [checkoutAddressList, setCheckoutAddressList] = useState<ShippingAddress[]>([]);
  const [checkoutAddVip, setCheckoutAddVip] = useState(false);
  const [checkoutVipPriceLine, setCheckoutVipPriceLine] = useState('₹299/mo');
  const [vipMonthlyProductId, setVipMonthlyProductId] = useState<number | null>(null);

  // Smart Feed State
  const useSmartFeed = true; // Always use smart feed API
  const [feedData, setFeedData] = useState<SmartFeedResponse | null>(null);
  const [feedStatus, setFeedStatus] = useState<FeedStatus | null>(null);
  const [userStatus, setUserStatus] = useState<UserStatus | null>(null);
  
  const cardRef = useRef<HTMLDivElement>(null);
  /** Synced immediately on pointer/touch down so move/end handlers never see stale React state (fixes mobile swipes). */
  const isDraggingRef = useRef(false);
  /** Pointer-down origin in viewport coords (clientX/Y). delta = current - start. */
  const pointerStartRef = useRef({ x: 0, y: 0 });
  /** Latest delta in CSS px, updated synchronously in pointermove; consumed by the rAF tick. */
  const dragDeltaRef = useRef({ x: 0, y: 0 });
  /** Tick id for the in-flight rAF; ensures only one paint per frame and lets us cancel on release. */
  const rafIdRef = useRef<number | null>(null);
  const activePointerIdRef = useRef<number | null>(null);
  const nextCardRef = useRef<HTMLDivElement>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const st = location.state as { reopenCart?: boolean } | undefined;
    if (st?.reopenCart) {
      setIsCartOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      const res = await fetchVipPlans(token);
      if (cancelled || !res.success || !res.data) return;
      if (typeof res.data.monthly.product_id === 'number') {
        setVipMonthlyProductId(res.data.monthly.product_id);
      }
      const m = res.data.monthly.amount_inr;
      setCheckoutVipPriceLine(`₹${Number(m).toLocaleString('en-IN')}/mo`);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!checkoutModalOpen || !token || user?.profile?.is_vip || vipMonthlyProductId == null) return;
    const has = cartItems.some((i) => i.id === vipMonthlyProductId);
    setCheckoutAddVip(has);
  }, [checkoutModalOpen, cartItems, vipMonthlyProductId, user?.profile?.is_vip, token]);

  // Load products from Smart Feed API
  useEffect(() => {
    if (isAuthenticated) {
      loadSmartFeed('initial_load');
      // Refresh user profile to get latest swipe counts and VIP status
      refreshUserProfile();
      // Check if we need to reset swipe limit for new day
      checkAndResetSwipeLimit();
      // Load cart items from backend
      loadCartFromBackend();
    }
  }, [isAuthenticated]);

  // Update total savings when cart items change
  useEffect(() => {
    const newTotalSavings = cartItems.reduce((sum, item) => {
      if (item.is_mystery_card) {
        return sum + (item.original_price - item.mystery_price);
      } else {
        return sum + (item.original_price - (item.mystery_price || item.display_price || 0));
      }
    }, 0);
    setTotalSavings(newTotalSavings);
  }, [cartItems]);

  // Debug isCartOpen state changes - removed for performance


  // Smart Feed Loading Function
  const loadSmartFeed = async (action: 'initial_load' | 'load_more' | 'refresh' = 'initial_load') => {
    if (!token) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch('/api/products/smart_feed/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
        },
        body: JSON.stringify({ action })
      });
      
        const data = await response.json();
      
      if (data.success) {
        setFeedData(data);
        setFeedStatus(data.feed_status);
        setUserStatus(data.user_status);
        setProducts(data.products);
        setCurrentIndex(0);
        
        // Update noProductsAvailable based on feed status and products array
        const hasProducts = data.products && data.products.length > 0;
        const hasMoreProducts = data.feed_status.has_more_products;
        
        // Show "Ready for More?" screen when:
        // 1. No products available AND no more products, OR
        // 2. User has seen all products (regardless of has_more_products)
        const shouldShowNoProductsScreen = !hasProducts || data.user_status?.has_seen_all_products;
        setNoProductsAvailable(shouldShowNoProductsScreen);
        
        // If no products available, clear current product to prevent swiping
        if (shouldShowNoProductsScreen) {
          setCurrentIndex(-1); // Set to -1 so currentProduct becomes undefined
        }
        
        console.log('Smart Feed Response:', data);
        console.log('Products count:', data.products?.length || 0);
        console.log('Has more products:', data.feed_status?.has_more_products);
        console.log('Feed Status:', data.feed_status);
        console.log('Show load more:', data.feed_status?.show_load_more);
        console.log('Show VIP upgrade:', data.feed_status?.show_vip_upgrade);
        console.log('Setting noProductsAvailable to:', shouldShowNoProductsScreen);
        } else {
        setError(data.error || 'Failed to load smart feed');
        // Smart feed failed, show error
        console.log('Smart feed failed');
        setError('Failed to load products from smart feed');
      }
    } catch (err) {
      setError('Network error: ' + (err as Error).message);
      // Smart feed error, show error
      console.log('Smart feed error');
      setError('Network error: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const currentProduct = currentIndex >= 0 && currentIndex < products.length ? products[currentIndex] : undefined;
  const nextProduct = nextProductIndex >= 0 && nextProductIndex < products.length ? products[nextProductIndex] : undefined;
  
  // Debug logging for stack effect (commented out for production)
  // console.log('Stack debug:', { 
  //   currentIndex, 
  //   nextProductIndex, 
  //   productsLength: products.length,
  //   hasCurrentProduct: !!currentProduct,
  //   hasNextProduct: !!nextProduct,
  //   currentProductName: currentProduct?.display_name,
  //   nextProductName: nextProduct?.display_name,
  //   currentProductImage: currentProduct?.display_image || currentProduct?.main_image,
  //   nextProductImage: nextProduct?.display_image || nextProduct?.main_image
  // });
  
  // Debug logging for current product (commented out for production)
  // console.log('Current product debug:', {
  //   currentIndex,
  //   productsLength: products.length,
  //   currentProduct: currentProduct ? { id: currentProduct.id, name: currentProduct.name } : null,
  //   noProductsAvailable
  // });

  // Debug swipe state (commented out for production)
  // useEffect(() => {
  //   console.log('Swipe state changed:', { isSwiping, isProcessingSwipe, isDragging });
  // }, [isSwiping, isProcessingSwipe, isDragging]);

  // Check product quantity before swiping
  const checkProductQuantity = async (productId: number) => {
    try {
      const response = await fetch(`/api/products/${productId}/check_quantity/`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to check quantity');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error checking quantity:', error);
      return { success: false, show_popup: false };
    }
  };

  // Handle quantity popup confirmation
  const handleQuantityConfirm = async (quantity: number) => {
    if (!pendingSwipeDirection || !currentProduct) return;
    
    // Add multiple items to cart based on quantity
    for (let i = 0; i < quantity; i++) {
      await addToBackendCart(currentProduct.id, 1, true);
    }
    
    // Proceed with the swipe
    await proceedWithSwipe(pendingSwipeDirection);
    
    // Reset pending state
    setPendingSwipeDirection(null);
  };

  // Handle quantity popup close
  const handleQuantityClose = () => {
    setIsQuantityPopupOpen(false);
    setQuantityPopupData(null);
    setPendingSwipeDirection(null);
  };

  // Proceed with swipe after quantity check
  const proceedWithSwipe = async (direction: 'left' | 'right') => {
    if (!currentProduct) return;
    
    setIsProcessingSwipe(true);
    
    try {
      const csrfToken = getCSRFToken();
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      // Add authentication token
      if (token) {
        headers['Authorization'] = `Token ${token}`;
      }
      
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      // Send swipe to backend
      const response = await fetch(`/api/products/${currentProduct.id}/swipe/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          direction: direction
        })
      });

      const data = await response.json();
      
      if (data.success) {
        if (direction === 'right') {
          // Add to backend cart instead of frontend state
          // Use replaceQuantity=true to avoid adding to existing quantity
          await addToBackendCart(currentProduct.id, 1, true);
          // Total savings will be recalculated when cart loads from backend
        }
        
        // Update daily swipe count in backend and local state
        if (user?.profile) {
          const newDailySwipes = (user.profile.daily_swipes || 0) + 1;
          
          // Update local state immediately for UI responsiveness
          updateDailySwipes(newDailySwipes);
        }
        
        // Refresh user profile from backend to sync data
        setTimeout(() => {
          refreshUserProfile();
          // Check if we need to reset swipe limit for new day
          checkAndResetSwipeLimit();
        }, 1000);
        
        // Animate card out based on swipe direction with smooth directional exit
        if (cardRef.current) {
          const outX = direction === 'right' ? 600 : -600;
          const outY = -50; // Slight upward movement for natural feel
          const rotation = direction === 'right' ? 25 : -25;
          cardRef.current.style.transition = 'transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s ease-out';
          cardRef.current.style.transform = `translate(${outX}px, ${outY}px) rotate(${rotation}deg) scale(0.7)`;
          cardRef.current.style.opacity = '0';
        }
        
        // Move to next product after animation (Simplified Bumble-style)
        setTimeout(() => {
          if (currentIndex < products.length - 1) {
            // Update indices immediately (no complex animation)
            const newCurrentIndex = currentIndex + 1;
            const newNextProductIndex = currentIndex + 2;
            
            setCurrentIndex(newCurrentIndex);
            setNextProductIndex(newNextProductIndex);
            
            // Reset card positions after React re-renders
            setTimeout(() => {
            if (cardRef.current) {
                cardRef.current.style.transition = 'none';
                cardRef.current.style.transform = 'translate(0px, 0px) scale(1)';
              cardRef.current.style.opacity = '1';
                cardRef.current.style.zIndex = '10';
              }
              
              if (nextCardRef.current) {
                nextCardRef.current.style.transition = 'none';
                nextCardRef.current.style.transform = 'translateY(8px) scale(0.95)';
                nextCardRef.current.style.opacity = '0.3';
                nextCardRef.current.style.zIndex = '0';
              }
              
              setIsProcessingSwipe(false);
            }, 100); // Small delay for React to render
          } else {
            // No more products
            setNoProductsAvailable(true);
            setCurrentIndex(-1);
            setIsProcessingSwipe(false);
          }
        }, 500); // Match exit animation duration
      } else {
        setIsProcessingSwipe(false);
        
        // Check if it's a swipe limit error
        if (data.message && data.message.includes('Daily swipe limit reached')) {
          setIsVIPModalOpen(true);
          // Don't clear cart items, just show VIP modal
          return;
        }
      }
    } catch (error) {
      // Still add to backend cart for better UX on other errors
      if (direction === 'right') {
        await addToBackendCart(currentProduct.id, 1, true);
        // Total savings will be recalculated when cart loads from backend
      }
      
      // Don't animate on error - just reset processing state
      setIsProcessingSwipe(false);
    }
  };

  const handleSwipe = async (direction: 'left' | 'right') => {
    // console.log('handleSwipe called with direction:', direction); // Debug: Swipe direction
    
    // Add haptic feedback for mobile devices
    if (navigator.vibrate) {
      navigator.vibrate(50); // Short vibration
    }
    
    if (isProcessingSwipe) {
      // console.log('Already processing swipe, ignoring'); // Debug: Duplicate swipe prevention
      return; // Prevent multiple swipes
    }
    
    // Check if no products are available
    if (noProductsAvailable) {
      // console.log('No products available, cannot swipe'); // Debug: No products check
      return;
    }
    
    // Check if there's a current product to swipe
    if (!currentProduct || currentIndex < 0 || currentIndex >= products.length) {
      // console.log('No current product to swipe or invalid index'); // Debug: Product validation
      return;
    }
    
    // Check if user can swipe (VIP logic)
    if (!user?.profile?.is_vip && (user?.profile?.daily_swipes || 0) >= 10) {
      setSwipeLimitReached(true);
      setIsVIPModalOpen(true);
      return;
    }
    
      // Check quantity for right swipes (likes)
    if (direction === 'right') {
      const quantityData = await checkProductQuantity(currentProduct.id);
      
      if (quantityData.success && quantityData.show_popup && !quantityData.is_mystery_card) {
        // Show quantity popup
        setQuantityPopupData({
          productName: quantityData.product_name,
          quantityAvailable: quantityData.quantity_available,
          message: quantityData.message,
          maxQuantity: quantityData.max_quantity
        });
        setPendingSwipeDirection(direction);
        setIsQuantityPopupOpen(true);
        return; // Don't proceed with swipe yet
      }
    }
    
    // Proceed with normal swipe
    await proceedWithSwipe(direction);
  };

  const getCSRFToken = () => {
    // Get CSRF token from Django cookie
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const handleBackToHome = () => {
    navigate('/');
  };


  const handleCartClick = async () => {
    console.log('Cart button clicked, validating cart availability');
    
    // First validate cart availability
    const validation = await validateCartAvailability();
    
    if (validation.hasIssues) {
      // Show validation modal with issues
      setCartValidationData({
        itemsToRemove: validation.itemsToRemove,
        itemsToAdjust: validation.itemsToAdjust
      });
      setIsCartValidationModalOpen(true);
    } else {
      // No issues, open cart directly
    setIsCartOpen(true);
    }
  };

  const handleRemoveFromCart = async (itemId: number) => {
    // Remove from backend cart - this will reload the cart and recalculate savings
    await removeFromBackendCart(itemId);
  };

  const handleUpdateQuantity = async (itemId: number, newQuantity: number) => {
    // Update quantity in backend cart - this will reload the cart and recalculate savings
    await updateBackendCartQuantity(itemId, newQuantity);
  };

  const handleCartValidationConfirm = async () => {
    // Remove unavailable items
    if (cartValidationData.itemsToRemove.length > 0) {
      const productIds = cartValidationData.itemsToRemove.map(item => item.product_id);
      await removeUnavailableItems(productIds);
    }
    
    // Adjust quantities for items with insufficient stock
    if (cartValidationData.itemsToAdjust.length > 0) {
      const adjustments = cartValidationData.itemsToAdjust.map(item => ({
        product_id: item.product_id,
        new_quantity: item.available_quantity
      }));
      await adjustCartQuantities(adjustments);
    }
    
    // Close modal and open cart
    setIsCartValidationModalOpen(false);
    setIsCartOpen(true);
  };

  const handleCartValidationCancel = () => {
    // Just close the modal without making changes
    setIsCartValidationModalOpen(false);
  };

  const handleCheckout = async () => {
    const fresh = await refreshUserProfile();
    const addrs = fresh?.shipping_addresses ?? [];
    if (addrs.length === 0) {
      setNeedAddressModalOpen(true);
      return;
    }
    await loadCartFromBackend();
    setCheckoutAddressList(addrs);
    const def = addrs.find((a) => a.is_default) || addrs[0];
    setSelectedAddressId(def.id);
    setCheckoutModalOpen(true);
  };

  const handleCheckoutVipToggle = async (next: boolean) => {
    if (!token || user?.profile?.is_vip) {
      setCheckoutAddVip(false);
      return;
    }
    if (vipMonthlyProductId == null) {
      toast.error('VIP product not loaded — try again.');
      return;
    }
    if (next) {
      const r = await addVipPlanToCart(token, vipMonthlyProductId);
      if (r.success) {
        setCheckoutAddVip(true);
        await loadCartFromBackend();
      } else {
        toast.error(r.message || 'Could not add VIP');
        setCheckoutAddVip(false);
      }
    } else {
      await removeCartProduct(token, vipMonthlyProductId);
      setCheckoutAddVip(false);
      await loadCartFromBackend();
    }
  };

  const confirmPlaceOrder = async () => {
    if (selectedAddressId == null || !token) return;
    setCheckoutSubmitting(true);
    try {
      const res = await fetch(getAuthApiUrl('/api/orders/create_order/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify({ shipping_address_id: selectedAddressId }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        toast.success(`Order placed! ${data.order_number ? `#${data.order_number}` : ''}`);
        setCheckoutModalOpen(false);
        setCheckoutAddVip(false);
        setIsCartOpen(false);
        await loadCartFromBackend();
        await refreshUserProfile();
      } else {
        toast.error(typeof data.message === 'string' ? data.message : 'Could not place order');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setCheckoutSubmitting(false);
    }
  };

  const checkoutCartSubtotal = cartItems.reduce(
    (sum, item) => sum + item.display_price * (item.quantity || 1),
    0
  );

  // Product Details Modal Handlers
  const handleCloseProductDetails = () => {
    setIsProductDetailsOpen(false);
    setSelectedProductId(null);
  };

  const handleAddToCartFromDetails = async (productId: number) => {
    // Add to cart logic - use replaceQuantity=true to avoid adding to existing quantity
    await addToBackendCart(productId, 1, true);
    
    // Refresh cart from backend
    await loadCartFromBackend();
    
    // Close the modal
    handleCloseProductDetails();
  };

  const handleContinueSwipingFromDetails = () => {
    handleCloseProductDetails();
  };

  const checkAndResetSwipeLimit = () => {
    if (user?.profile) {
      const today = new Date().toISOString().split('T')[0];
      const lastSwipeDate = user.profile.last_swipe_date;
      
      // If it's a new day, reset the swipe limit
      if (lastSwipeDate !== today) {
        setSwipeLimitReached(false);
      }
    }
  };

  // ─── Unified Pointer-Events drag system ──────────────────────────────────
  // Works for mouse, touch, and pen with one code path. Pointer capture keeps
  // events flowing to the card even if the finger leaves the bounds. All
  // transform writes are coalesced into a single rAF tick to avoid jank.

  /** Apply the latest dragDeltaRef to the card transform. Runs once per frame. */
  const paintDragFrame = useCallback(() => {
    rafIdRef.current = null;
    const el = cardRef.current;
    if (!el || !isDraggingRef.current) return;

    const { x: dx, y: dy } = dragDeltaRef.current;
    // Allow generous horizontal travel; damp vertical so the card stays "on the table".
    const clampedX = Math.max(-260, Math.min(260, dx));
    const clampedY = Math.max(-80, Math.min(80, dy * 0.5));

    // Use the card's own width as the rotation reference so the gesture feels
    // consistent across screen sizes. ~18deg at full throw.
    const cardWidth = el.offsetWidth || 320;
    const rotateDeg = (clampedX / cardWidth) * 18;

    // Linear ramp on a 0..1 progress so visual feedback never "jumps".
    const progress = Math.min(Math.abs(clampedX) / 140, 1);
    const scale = 1 - progress * 0.04;
    const opacity = 1 - progress * 0.15;

    el.style.transform = `translate3d(${clampedX}px, ${clampedY}px, 0) rotate(${rotateDeg}deg) scale(${scale})`;
    el.style.opacity = opacity.toFixed(3);

    const leftIndicator = el.querySelector('.swipe-left-indicator') as HTMLElement | null;
    const rightIndicator = el.querySelector('.swipe-right-indicator') as HTMLElement | null;
    if (leftIndicator) leftIndicator.style.opacity = clampedX < 0 ? progress.toFixed(3) : '0';
    if (rightIndicator) rightIndicator.style.opacity = clampedX > 0 ? progress.toFixed(3) : '0';
  }, []);

  const scheduleDragPaint = useCallback(() => {
    if (rafIdRef.current != null) return;
    rafIdRef.current = window.requestAnimationFrame(paintDragFrame);
  }, [paintDragFrame]);

  const resetCardToCenter = useCallback((animated: boolean) => {
    const el = cardRef.current;
    if (!el) return;
    if (rafIdRef.current != null) {
      window.cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (animated) {
      el.style.transition = 'transform 280ms cubic-bezier(0.2, 0.9, 0.25, 1), opacity 200ms ease-out';
    } else {
      el.style.transition = 'none';
    }
    el.style.transform = 'translate3d(0, 0, 0) rotate(0deg) scale(1)';
    el.style.opacity = '1';
    const left = el.querySelector('.swipe-left-indicator') as HTMLElement | null;
    const right = el.querySelector('.swipe-right-indicator') as HTMLElement | null;
    if (left) left.style.opacity = '0';
    if (right) right.style.opacity = '0';
    if (animated) {
      window.setTimeout(() => {
        if (cardRef.current) cardRef.current.style.transition = '';
      }, 300);
    }
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isProcessingSwipe || !cardRef.current) return;
    // Ignore secondary mouse buttons; only the primary pointer drags.
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    isDraggingRef.current = true;
    activePointerIdRef.current = e.pointerId;
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    dragDeltaRef.current = { x: 0, y: 0 };

    cardRef.current.classList.add('swiping');
    // Drop any in-flight snap-back transition so the card sticks to the finger immediately.
    cardRef.current.style.transition = 'none';

    try {
      cardRef.current.setPointerCapture(e.pointerId);
    } catch {
      /* setPointerCapture can throw if the element is detached; ignore. */
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) return;

    dragDeltaRef.current = {
      x: e.clientX - pointerStartRef.current.x,
      y: e.clientY - pointerStartRef.current.y,
    };
    scheduleDragPaint();
  };

  const finishDrag = (commitDirection: 'left' | 'right' | null) => {
    if (!cardRef.current) return;
    isDraggingRef.current = false;
    cardRef.current.classList.remove('swiping');
    activePointerIdRef.current = null;

    if (commitDirection) {
      // Don't reset; proceedWithSwipe owns the exit animation.
      if (rafIdRef.current != null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      handleSwipe(commitDirection);
    } else {
      resetCardToCenter(true);
    }
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    if (activePointerIdRef.current !== null && e.pointerId !== activePointerIdRef.current) return;

    const { x: dx, y: dy } = dragDeltaRef.current;
    const cardWidth = cardRef.current?.offsetWidth || 320;
    // Commit at ~28% of card width OR a clear horizontal angle past 60px.
    const DISTANCE_COMMIT = Math.max(cardWidth * 0.28, 70);
    const ANGLE_LIMIT_DEG = 55;
    const isHorizontal = Math.abs(dx) > Math.abs(dy) * 0.6; // permissive

    if (Math.abs(dx) > DISTANCE_COMMIT && isHorizontal) {
      const angle = Math.abs(Math.atan2(Math.abs(dy), Math.abs(dx)) * 180 / Math.PI);
      if (angle < ANGLE_LIMIT_DEG) {
        finishDrag(dx > 0 ? 'right' : 'left');
        return;
      }
    }
    finishDrag(null);
  };

  const handlePointerCancel = () => {
    if (!isDraggingRef.current) return;
    finishDrag(null);
  };

  useEffect(() => {
    return () => {
      if (rafIdRef.current != null) window.cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }



  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="cosmic-card p-8 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gradient">Loading Cosmic Products...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="cosmic-card p-8 text-center">
          <h2 className="text-xl font-semibold text-red-400 mb-4">Error Loading Products</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button onClick={() => loadSmartFeed('refresh')} className="btn-cosmic">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (noProductsAvailable) {
    return (
      <>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <Header
          onRefresh={() => loadSmartFeed('refresh')}
          totalProducts={products.length}
          currentIndex={currentIndex}
        />
        
        {/* Smart Button Screen */}
        <div className="flex items-center justify-center pt-20">
        <div className="cosmic-card p-8 text-center max-w-md">
            <div className="mb-6">
              <div className="text-6xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold mb-2">Ready for More?</h3>
              
              {/* Dynamic message based on feed status */}
              {feedStatus ? (
                <p className="text-muted-foreground">
                  {feedStatus.message}
                </p>
              ) : (
                <p className="text-muted-foreground">
            You've swiped through all available products. 
          </p>
              )}
            </div>
          
          <div className="space-y-3">
              {/* Smart Load More Products Button */}
              {feedStatus?.show_load_more && (
            <button 
                  onClick={() => loadSmartFeed('load_more')}
                  className="w-full btn-cosmic flex items-center justify-center gap-2"
            >
                  <span>🔄</span>
              Load More Products
            </button>
              )}
            
              {/* VIP Upgrade Button */}
              {feedStatus?.show_vip_upgrade && (
              <button 
                onClick={() => setIsVIPModalOpen(true)} 
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center gap-2"
              >
                  <span>👑</span>
                  Upgrade to VIP for Unlimited Swipes
              </button>
            )}
            
              {/* Quick Checkout Button */}
              {cartItems.length > 0 && (
            <button 
                  onClick={() => setIsCartOpen(true)}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all flex items-center justify-center gap-2"
            >
                  <span>⚡</span>
                  Quick Checkout ({cartItems.length} items)
            </button>
              )}
              
              {/* Fallback button if no other buttons show */}
              {!feedStatus?.show_load_more && !feedStatus?.show_vip_upgrade && cartItems.length === 0 && (
                <button 
                  onClick={() => loadSmartFeed('refresh')}
                  className="w-full btn-cosmic flex items-center justify-center gap-2"
                >
                  <span>🔄</span>
                  Refresh Products
                </button>
              )}

          </div>
        </div>
      </div>
      </div>
      
      {/* Cart Overlay */}
      <CartOverlay
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onCheckout={handleCheckout}
        onRemoveItem={handleRemoveFromCart}
        onUpdateQuantity={handleUpdateQuantity}
      />
      <NeedAddressModal
        isOpen={needAddressModalOpen}
        onClose={() => setNeedAddressModalOpen(false)}
      />
      <CheckoutAddressModal
        isOpen={checkoutModalOpen}
        onClose={() => setCheckoutModalOpen(false)}
        addresses={checkoutAddressList}
        selectedId={selectedAddressId}
        onSelectId={setSelectedAddressId}
        cartTotal={checkoutCartSubtotal}
        shippingFee={CHECKOUT_SHIPPING_FEE}
        onConfirm={confirmPlaceOrder}
        isSubmitting={checkoutSubmitting}
        showVipUpsell={!user?.profile?.is_vip}
        vipPriceLine={checkoutVipPriceLine}
        addVipSelected={checkoutAddVip}
        onAddVipSelectedChange={handleCheckoutVipToggle}
      />
      <VIPUpgradeModal
        isOpen={isVIPModalOpen}
        onClose={() => setIsVIPModalOpen(false)}
        onAddedToCart={async () => {
          await loadCartFromBackend();
        }}
        message={
          swipeLimitReached
            ? "You've used all 10 free swipes today. VIP gives you unlimited swipes every day."
            : 'Unlimited swipes, exclusive deals, and return credit on your plan.'
        }
      />
      <Toaster position="top-center" />
      </>
    );
  }

  if (!currentProduct) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="cosmic-card p-8 text-center">
          <h3 className="text-xl font-semibold mb-4">No More Products!</h3>
          <p className="text-muted-foreground mb-4">
            You've swiped through all available products. 
          </p>
          <button onClick={() => loadSmartFeed('refresh')} className="btn-cosmic">
            Refresh Products
            </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* New Header Component */}
        <Header
          onRefresh={() => loadSmartFeed('refresh')}
          totalProducts={products.length}
          currentIndex={currentIndex}
        />

        {/* Swipe Card with Stack Effect */}
        <div className="flex justify-center">
          {swipeLimitReached ? (
            <div className="cosmic-card p-6 sm:p-8 max-w-sm sm:max-w-md w-full text-center mx-4">
              <div className="mb-6">
                <Crown className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-yellow-400 mb-4" />
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Daily Swipe Limit Reached!</h2>
                <p className="text-sm sm:text-base text-gray-300 mb-4 px-2">
                  You've used all 10 free swipes today. Come back tomorrow or upgrade to VIP for unlimited swiping!
                </p>
              </div>
              
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setIsVIPModalOpen(true)}
                  className="w-full bg-gradient-to-r from-cosmic-primary to-cosmic-secondary text-white font-semibold py-3 px-6 rounded-lg hover:from-cosmic-secondary hover:to-cosmic-primary transition-all duration-300 text-sm sm:text-base"
                >
                  View Jaalu VIP
                </button>
                
                <button
                  onClick={handleBackToHome}
                  className="w-full bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-gray-700 transition-colors text-sm sm:text-base"
                >
                  Back to Home
                </button>
              </div>
            </div>
          ) : (
            <div className="relative">
              {/* Next Product Preview (Bumble-style behind current) */}
              {nextProduct && (
                <div 
                  ref={nextCardRef}
                  className="absolute cosmic-card p-6 max-w-sm w-full opacity-30 scale-95 transform translate-y-2 z-0 transition-all duration-300 ease-out"
                  style={{
                    transform: 'translateY(8px) scale(0.95)',
                    zIndex: 0
                  }}
                >
                  <div className="bg-cosmic-card rounded-2xl shadow-lg p-6 h-full">
                    {/* Next Product Image */}
                    <div className="relative mb-4">
                      <img
                        src={nextProduct.display_image || nextProduct.main_image}
                        alt={nextProduct.display_name}
                        className="w-full h-48 object-cover rounded-xl"
                      />
                      {nextProduct.is_mystery_card && (
                        <div className="absolute top-2 left-2 bg-gradient-to-r from-secondary to-mystery-glow text-white px-2 py-1 rounded-full text-xs font-medium">
                          Mystery
                        </div>
                      )}
                    </div>
                    
                    {/* Next Product Info */}
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">
                        {nextProduct.display_name}
                      </h3>
                      <div className="flex items-center justify-center gap-2 text-sm text-gray-300 mb-2">
                        <span className="line-through">₹{formatRupee(nextProduct.original_price)}</span>
                        <span className="text-primary font-bold">₹{formatRupee(nextProduct.display_price)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Current Product (on top) */}
            <div
              ref={cardRef}
                className={`swipe-card cosmic-card p-6 max-w-sm w-full relative z-10 ${
                isProcessingSwipe ? 'pointer-events-none' : 'cursor-grab active:cursor-grabbing'
              }`}
              style={{ willChange: 'transform', WebkitTapHighlightColor: 'transparent' }}
              onPointerDown={isProcessingSwipe ? undefined : handlePointerDown}
              onPointerMove={isProcessingSwipe ? undefined : handlePointerMove}
              onPointerUp={isProcessingSwipe ? undefined : handlePointerUp}
              onPointerCancel={isProcessingSwipe ? undefined : handlePointerCancel}
            >
            {/* Swipe Direction Indicators */}
            <div className="absolute inset-0 pointer-events-none z-20">
              {/* Left Swipe Indicator */}
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-red-500/30 backdrop-blur-sm border-2 border-red-400/50 rounded-xl px-4 py-3 text-red-300 text-base font-bold opacity-0 transition-all duration-200 swipe-left-indicator shadow-lg">
                <div className="flex items-center gap-2">
                  <X className="w-5 h-5" />
                  <span>DISLIKE</span>
                </div>
              </div>
              
              {/* Right Swipe Indicator */}
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-green-500/30 backdrop-blur-sm border-2 border-green-400/50 rounded-xl px-4 py-3 text-green-300 text-base font-bold opacity-0 transition-all duration-200 swipe-right-indicator shadow-lg">
                <div className="flex items-center gap-2">
                  <span>LIKE</span>
                  <Heart className="w-5 h-5" />
                </div>
              </div>
            </div>

            {/* Product Badge */}
            {currentProduct.is_mystery_card ? (
              <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-secondary to-mystery-glow text-white px-3 py-1 rounded-full text-sm font-medium">
                Mystery Item
              </div>
            ) : currentProduct.discount_percentage > 0 ? (
              <div className="absolute top-4 right-4 z-10 bg-gradient-to-r from-primary to-secondary text-white px-3 py-1 rounded-full text-sm font-medium">
                {currentProduct.discount_percentage}% OFF
              </div>
            ) : null}

            {/* Product Image - Compact for Mobile */}
            <div className="relative mb-4 sm:mb-6">
              <img
                src={currentProduct.display_image || currentProduct.main_image}
                alt={currentProduct.display_name}
                className="w-full h-48 sm:h-64 object-cover rounded-2xl pointer-events-none"
                draggable={false}
                onError={(e) => {
                  // Fallback to mystery image if display image fails
                  const target = e.target as HTMLImageElement;
                  target.src = currentProduct.mystery_image || 'https://via.placeholder.com/400x300/1a1a2e/ffffff?text=Mystery+Item';
                }}
              />
              {currentProduct.is_mystery_card && (
                <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center pointer-events-none">
                  <div className="text-center p-4">
                    <Sparkles className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-2 sm:mb-4 text-mystery-glow" />
                    <p className="text-base sm:text-lg font-semibold text-white">Mystery Item</p>
                    {currentProduct.mystery_hint && (
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1 sm:mt-2">{currentProduct.mystery_hint}</p>
                    )}
                  </div>
                </div>
              )}
              {/* Details button — small dedicated tap target so the swipe gesture owns the rest of the card. */}
              <button
                type="button"
                aria-label="View product details"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedProductId(currentProduct.id);
                  setIsProductDetailsOpen(true);
                }}
                className="absolute top-2 right-2 z-30 w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/80 transition-colors shadow-md"
              >
                <Info className="w-4 h-4" />
              </button>
            </div>

            {/* Product Info - Compact for Mobile */}
            <div className="text-center">
              <h3 className="text-lg sm:text-xl font-semibold mb-1 sm:mb-2 line-clamp-2">{currentProduct.display_name}</h3>
              <p className="text-muted-foreground text-xs sm:text-sm mb-2 sm:mb-4 line-clamp-2">{currentProduct.description}</p>
              
              {/* Compact horizontal layout for mobile */}
              <div className="flex items-center justify-center space-x-2 sm:space-x-4 mb-2 sm:mb-4 text-xs sm:text-sm">
                <span className="text-muted-foreground">{currentProduct.category.name}</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground">{currentProduct.vendor.name}</span>
                <span className="text-muted-foreground">•</span>
                <div className="flex items-center space-x-1">
                  <Star className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400 fill-current" />
                  <span className="text-muted-foreground">4.8</span>
              </div>
              </div>

              {/* Compact Pricing for Mobile */}
              <div className="flex items-center justify-center space-x-3 mb-4 sm:mb-6">
                <div className="text-center">
                  {/* Current Price */}
                  <span className="text-2xl sm:text-3xl font-bold text-cosmic-primary">
                    ₹{formatRupee(currentProduct.mystery_price || currentProduct.display_price || 0)}
                  </span>
                  
                  {/* Price Comparison (if there's a discount) - Compact for Mobile */}
                  {(() => {
                    const sale = rupeeAmount(currentProduct.mystery_price || currentProduct.display_price);
                    const orig = rupeeAmount(currentProduct.original_price);
                    return orig > 0 && orig > sale;
                  })() && (
                    <div className="flex flex-col sm:flex-col items-center gap-1 mt-1">
                      {/* Original Price (Struck through) */}
                      <span className="text-sm sm:text-lg text-gray-400 line-through">
                        ₹{formatRupee(currentProduct.original_price)}
                      </span>
                      
                      {/* Discount Badge - Smaller for Mobile */}
                      <div className="px-2 py-1 sm:px-3 sm:py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-full">
                        {Math.round(
                          ((rupeeAmount(currentProduct.original_price) -
                            rupeeAmount(currentProduct.mystery_price || currentProduct.display_price)) /
                            rupeeAmount(currentProduct.original_price)) *
                            100
                        )}
                        % OFF
                      </div>
                      
                      {/* Savings Amount - Smaller for Mobile */}
                      <span className="text-xs sm:text-sm text-green-400 font-medium">
                        Save ₹
                        {(
                          rupeeAmount(currentProduct.original_price) -
                          rupeeAmount(currentProduct.mystery_price || currentProduct.display_price)
                        ).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                  
                  {/* No Discount Message */}
                  {(() => {
                    const sale = rupeeAmount(currentProduct.mystery_price || currentProduct.display_price);
                    const orig = rupeeAmount(currentProduct.original_price);
                    return orig > 0 && orig === sale;
                  })() && (
                    <div className="mt-1">
                      <span className="text-xs sm:text-sm text-gray-400">
                        Best Price Available
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Swipe Instructions - REMOVED (intuitive for users) */}
          </div>
          </div>
          )}
        </div>

        {/* Swipe Action Buttons - Compact for Mobile */}
        {!swipeLimitReached && (
          <div className="flex justify-center items-center space-x-4 sm:space-x-6">
            {/* Dislike Button */}
            <button
              onClick={() => !isProcessingSwipe && handleSwipe('left')}
              disabled={isProcessingSwipe}
              className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-white transition-colors ${
                isProcessingSwipe 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-red-500 hover:bg-red-600'
              }`}
            >
              <X className="w-6 h-6 sm:w-8 sm:h-8" />
            </button>
            
            {/* Quick Cart Access Button */}
            <button
              onClick={handleCartClick}
              className="relative w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-white transition-all duration-200 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-blue-600 hover:to-purple-600 hover:scale-105 active:scale-95 shadow-lg border-2 border-purple-400/30"
              title="View cart items"
            >
              <ShoppingCart className="w-6 h-6 sm:w-8 sm:h-8" />
              {cartItems.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold shadow-md border border-red-400">
                  {cartItems.length}
                </span>
              )}
            </button>
            
            {/* Like Button */}
            <button
              onClick={() => !isProcessingSwipe && handleSwipe('right')}
              disabled={isProcessingSwipe}
              className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-white transition-colors ${
                isProcessingSwipe 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-500 hover:bg-green-600'
              }`}
            >
              <Heart className="w-6 h-6 sm:w-8 sm:h-8" />
            </button>
          </div>
        )}

        {/* Savings Info Below Buttons - Seamless */}
        <div className="flex justify-center mt-3 sm:mt-4">
          <div className="flex items-center gap-2 sm:gap-3">
              <span className="text-2xl">💰</span>
            <span className="text-green-400 font-semibold text-base sm:text-lg bg-green-400/10 px-3 py-1 rounded-full border border-green-400/30">
              ₹{totalSavings.toLocaleString()} saved
              </span>
            </div>
          </div>
      </div>

      {/* Cart Overlay */}
              <CartOverlay
          isOpen={isCartOpen}
          onClose={() => setIsCartOpen(false)}
          cartItems={cartItems}
          onCheckout={handleCheckout}
          onRemoveItem={handleRemoveFromCart}
        onUpdateQuantity={handleUpdateQuantity}
        />
      <NeedAddressModal
        isOpen={needAddressModalOpen}
        onClose={() => setNeedAddressModalOpen(false)}
      />
      <CheckoutAddressModal
        isOpen={checkoutModalOpen}
        onClose={() => setCheckoutModalOpen(false)}
        addresses={checkoutAddressList}
        selectedId={selectedAddressId}
        onSelectId={setSelectedAddressId}
        cartTotal={checkoutCartSubtotal}
        shippingFee={CHECKOUT_SHIPPING_FEE}
        onConfirm={confirmPlaceOrder}
        isSubmitting={checkoutSubmitting}
        showVipUpsell={!user?.profile?.is_vip}
        vipPriceLine={checkoutVipPriceLine}
        addVipSelected={checkoutAddVip}
        onAddVipSelectedChange={handleCheckoutVipToggle}
      />
      <Toaster position="top-center" />

      <VIPUpgradeModal
        isOpen={isVIPModalOpen}
        onClose={() => setIsVIPModalOpen(false)}
        onAddedToCart={async () => {
          await loadCartFromBackend();
        }}
        message={
          swipeLimitReached
            ? "You've used all 10 free swipes today. VIP gives you unlimited swipes every day."
            : 'Unlimited swipes, exclusive deals, and return credit on your plan.'
        }
      />

      {/* Cart Validation Modal */}
      <CartValidationModal
        isOpen={isCartValidationModalOpen}
        onClose={handleCartValidationCancel}
        onConfirm={handleCartValidationConfirm}
        itemsToRemove={cartValidationData.itemsToRemove}
        itemsToAdjust={cartValidationData.itemsToAdjust}
      />

      {/* Quantity Popup */}
      {quantityPopupData && (
        <QuantityPopup
          isOpen={isQuantityPopupOpen}
          onClose={handleQuantityClose}
          onConfirm={handleQuantityConfirm}
          productName={quantityPopupData.productName}
          quantityAvailable={quantityPopupData.quantityAvailable}
          message={quantityPopupData.message}
          maxQuantity={quantityPopupData.maxQuantity}
        />
      )}

      {/* Product Details Modal */}
        <ProductDetailsModal
          isOpen={isProductDetailsOpen}
        onClose={handleCloseProductDetails}
        productId={selectedProductId}
          onAddToCart={handleAddToCartFromDetails}
        onContinueSwiping={handleContinueSwipingFromDetails}
        />
    </div>
    </>
  );
};


